/**
 * 종목 요약(basic) mock 합성 — 서버 `GET /tickers/search` 응답에 `basic` 필드 추가되기 전까지
 * 자동완성/검색결과 공통으로 사용하는 fallback.
 *
 * 실 API 연동 시 이 파일은 제거해도 되지만, 호출부에서 `result.basic ?? mockBasic(...)` 패턴으로
 * 안전하게 유지한다.
 */

export type TickerAssetType = 'stock' | 'coin' | 'us_stock';

/** 자동완성·검색에 표시할 최소 요약 (현재가 · 등락률 · PER · 시가총액) */
export interface TickerBasicSummary {
  /** 현재가 — 주식 KRW, 코인 KRW, 미장 USD */
  price: number;
  /** 등락률 (%) */
  changePct: number;
  /** PER (코인은 null — 시총 개념 대신 "—" 로 표시) */
  per: number | null;
  /** 시가총액 표시 문자열 (예: "350.2조원", "$4.2T", "—") */
  marketCap: string;
}

const KNOWN_BASIC: Record<string, TickerBasicSummary> = {
  // ── 국내주식 (KRW) ─────────────────────────────
  '005930': { price: 58_600,  changePct: 1.21,  per: 12.8, marketCap: '350.2조원' },
  '000660': { price: 185_000, changePct: 0.54,  per: 15.2, marketCap: '135.0조원' },
  '373220': { price: 410_000, changePct: -0.85, per: 45.3, marketCap: '95.8조원' },
  '207940': { price: 950_000, changePct: 1.52,  per: 88.5, marketCap: '68.0조원' },
  '005380': { price: 250_000, changePct: 0.82,  per: 5.8,  marketCap: '53.1조원' },
  '000270': { price: 110_000, changePct: -0.45, per: 6.1,  marketCap: '44.5조원' },
  '051910': { price: 380_000, changePct: 1.10,  per: 18.9, marketCap: '26.9조원' },
  '006400': { price: 340_000, changePct: -1.45, per: 22.4, marketCap: '23.4조원' },
  '068270': { price: 220_000, changePct: 0.31,  per: 35.8, marketCap: '32.0조원' },
  '035420': { price: 220_000, changePct: -1.21, per: 25.4, marketCap: '36.3조원' },
  '035720': { price: 52_000,  changePct: 0.31,  per: 38.2, marketCap: '23.0조원' },
  '105560': { price: 98_000,  changePct: 0.82,  per: 8.5,  marketCap: '38.6조원' },
  '055550': { price: 55_000,  changePct: 0.45,  per: 7.8,  marketCap: '28.4조원' },

  // ── 코인 (PER 없음) ─────────────────────────────
  'KRW-BTC':  { price: 145_000_000, changePct: 0.52,  per: null, marketCap: '—' },
  'KRW-ETH':  { price: 5_200_000,   changePct: -0.31, per: null, marketCap: '—' },
  'KRW-XRP':  { price: 3_500,       changePct: 1.21,  per: null, marketCap: '—' },
  'KRW-SOL':  { price: 280_000,     changePct: 2.14,  per: null, marketCap: '—' },
  'KRW-DOGE': { price: 350,         changePct: -1.80, per: null, marketCap: '—' },

  // ── 미장 (USD) ─────────────────────────────
  AAPL:  { price: 273.05, changePct: 1.04,  per: 32.5, marketCap: '$4.2T' },
  MSFT:  { price: 418.07, changePct: -1.12, per: 36.1, marketCap: '$3.1T' },
  NVDA:  { price: 202.06, changePct: 0.19,  per: 68.2, marketCap: '$5.1T' },
  AMZN:  { price: 248.28, changePct: -0.91, per: 45.8, marketCap: '$2.6T' },
  GOOGL: { price: 337.42, changePct: -1.25, per: 27.3, marketCap: '$4.1T' },
  META:  { price: 670.91, changePct: -2.56, per: 28.4, marketCap: '$1.7T' },
  TSLA:  { price: 392.50, changePct: -2.03, per: 95.6, marketCap: '$1.2T' },
  AVGO:  { price: 399.63, changePct: -1.70, per: 42.8, marketCap: '$1.8T' },
  JPM:   { price: 316.99, changePct: 2.16,  per: 14.2, marketCap: '$895B' },
  LLY:   { price: 919.90, changePct: -0.77, per: 72.1, marketCap: '$870B' },
};

/** 코드를 받아 0~1 사이의 결정적 해시 값 — 모르는 종목도 일관된 값 생성 */
function hash01(code: string, salt = 0): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < code.length; i++) h = (h * 16777619) ^ code.charCodeAt(i);
  return ((h >>> 0) / 0xffffffff);
}

/**
 * code + assetType 을 받아 요약 값을 반환. 알려진 종목은 실값, 아니면 코드 해시 기반 합성.
 * 같은 입력은 항상 같은 값 반환 (UI 깜빡임 방지).
 */
export function mockBasic(code: string, assetType: TickerAssetType): TickerBasicSummary {
  const known = KNOWN_BASIC[code];
  if (known) return known;

  if (assetType === 'coin') {
    const price = Math.round(100 + hash01(code, 1) * 100_000_000);
    const changePct = Number(((hash01(code, 2) - 0.5) * 6).toFixed(2));
    return { price, changePct, per: null, marketCap: '—' };
  }
  if (assetType === 'us_stock') {
    const price = Number((5 + hash01(code, 1) * 500).toFixed(2));
    const changePct = Number(((hash01(code, 2) - 0.5) * 6).toFixed(2));
    const per = Number((8 + hash01(code, 3) * 40).toFixed(1));
    const capB = Math.round(5 + hash01(code, 4) * 500);
    return {
      price,
      changePct,
      per,
      marketCap: capB >= 1000 ? `$${(capB / 1000).toFixed(1)}T` : `$${capB}B`,
    };
  }
  // stock 기본 (KOSPI/KOSDAQ)
  const price = Math.round(5_000 + hash01(code, 1) * 500_000);
  const changePct = Number(((hash01(code, 2) - 0.5) * 4).toFixed(2));
  const per = Number((5 + hash01(code, 3) * 40).toFixed(1));
  const capEok = Math.round(500 + hash01(code, 4) * 500_000);
  const marketCap =
    capEok >= 10_000
      ? `${(capEok / 10_000).toFixed(1)}조원`
      : `${capEok.toLocaleString('ko-KR')}억원`;
  return { price, changePct, per, marketCap };
}

/** 자산별 가격 표시 — 주식·코인 KRW(원), 미장 USD($) */
export function formatTickerPrice(basic: TickerBasicSummary, assetType: TickerAssetType): string {
  if (assetType === 'us_stock') {
    return `$${basic.price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return `${basic.price.toLocaleString('ko-KR')}원`;
}

/** 시장 배지 색상 매핑 */
export function marketBadgeClass(market: string): string {
  const upper = (market || '').toUpperCase();
  if (upper === 'KOSPI') return 'bg-blue-100 text-blue-700';
  if (upper === 'KOSDAQ') return 'bg-emerald-100 text-emerald-700';
  if (upper === 'NASDAQ' || upper === 'US') return 'bg-purple-100 text-purple-700';
  if (upper === 'NYSE') return 'bg-indigo-100 text-indigo-700';
  if (upper === 'UPBIT') return 'bg-amber-100 text-amber-700';
  return 'bg-gray-100 text-gray-700';
}

/**
 * market 필드가 없는 응답(예: /search) 에서 assetType 기반 기본 시장 추정.
 * - coin → UPBIT / us_stock → NASDAQ / stock → KOSPI (default)
 * 코드는 향후 KOSDAQ 판별 등에 쓰일 수 있어 파라미터만 남겨둠 (현재 미사용).
 */
export function deriveMarket(_code: string, assetType: TickerAssetType): string {
  if (assetType === 'coin') return 'UPBIT';
  if (assetType === 'us_stock') return 'NASDAQ';
  return 'KOSPI';
}

/** 등락률 → tailwind 클래스 (한국 증시 컨벤션: 상승 빨강 / 하락 파랑) */
export function changeColorClass(changePct: number): string {
  if (changePct > 0) return 'text-red-500';
  if (changePct < 0) return 'text-blue-500';
  return 'text-[var(--color-text-sub)]';
}
